import React, { useEffect, useRef, useState } from "react";
import { ERAS_LISTED, eraRegionIds, eraYears, erasInView } from "./eras";
import { canvasImage, craftCounter, eventMarker, landCounter, navalShape, pulseFrame } from "./map_icons";
import { BLUE, GREEN, ORANGE, RED, YELLOW, usualSideColor } from "./side_colors";

// History map: a flat, Google Maps-style slippy map (MapLibre GL, Web
// Mercator) with polities, occupations, events and moving military units
// for a date. Data for a span of dates is buffered from /globe/* and
// filtered here day by day, so playback steps one day at a time without a
// request per step.

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DAY_MS = 86400000;
const MIN_DATE = "1706-01-01";
const MAX_DATE = "2026-12-31";
const SHAPES_PER_REQUEST = 200; // GlobeController::BOUNDARY_SHAPES_LIMIT

// Base map: OpenFreeMap vector tiles (OpenMapTiles schema), drawn with only
// physical layers (water, rivers, land cover) and a few place names, so the
// historical borders are the only political lines. More detail appears as
// you zoom in. If the tiles fail to load, Natural Earth land from
// /globe/land is drawn instead.
const TILES_URL = "https://tiles.openfreemap.org/planet";
const GLYPHS_URL = "https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf";
const FONT = ["Noto Sans Regular"];

const COLORS = {
  land: "#1c2537",
  water: "#0e1522",
  waterLine: "#1f3554",
  landcover: "#202b40",
  ice: "#28334a",
  text: "#dfe6f3",
  halo: "rgba(6,9,15,0.9)",
  muted: "#8e9aaf",
};

const ICON_COLORS = {
  explosion: "#e5484d", // wars / invasions
  sword: "#f76b15", // battles / sieges
  scroll: "#46a758", // treaties
  fist: "#eec74f", // uprisings / coups
  knife: "#d6409f", // assassinations
  skull: "#b5a48b", // mass death
  gavel: "#8da4ef", // trials
  rocket: "#5eb1ef", // science
  danger: "#a18072", // crises
};
const DEFAULT_EVENT_COLOR = "#8e9aaf";
// Wars outrank battles outrank everything else when labels collide.
const ICON_WEIGHT = { explosion: 3, sword: 2, fist: 1.5, scroll: 1.5 };

const CONTROL_COLORS = {
  sovereign: "#6f80a6",
  occupied: "#d0675d",
  administered: "#4d9dc0",
  leased: "#8c6fb8",
  condominium: "#b8860b",
};

// Polity fill colors, assigned by entity id so a country keeps its color.
const POLITY_PALETTE = ["#5b7bd5", "#c46a5b", "#5fa77f", "#b58b3c", "#8f6cc4", "#4fa3b5", "#c45f94", "#7f9a45"];

// Force colors, one per side of the wars on screen (see forceColors).
const FORCE_PALETTE = [RED, BLUE, YELLOW, "#b18cff", GREEN, ORANGE, "#f27ad8", "#c9d36b"];
const FORCE_NO_ENTITY = "#c7ced9";

// Bigger formations win label collisions and draw bigger icons.
const UNIT_RANK = {
  army_group: 9, navy: 9, air_force: 8, army: 8, fleet: 8, corps: 7, task_force: 7, squadron: 6,
  division: 6, flotilla: 5, garrison: 5, brigade: 5, air_unit: 5, regiment: 4, detachment: 4, battalion: 3, ship: 3,
};
const UNIT_KIND_LABELS = { army_group: "army group", task_force: "task force", air_force: "air force", air_unit: "air unit" };
const TRAIL_DAYS = 24; // length of the fading trail behind a moving unit
const TRAIL_STEPS = 8;
const PULSE_FRAMES = 12; // frames of the ring around battles under way
const PULSE_MS = 90;

const SPEEDS = [
  { days: 1, label: "1 day/s" },
  { days: 5, label: "5 days/s" },
  { days: 10, label: "10 days/s" },
  { days: 30, label: "1 month/s" },
  { days: 90, label: "3 months/s" },
];

// ---------------------------------------------------------------------------
// Dates: the map keeps time as fractional days since 1970-01-01 (UTC)
// ---------------------------------------------------------------------------

const isoToDay = (iso) => Date.parse(`${iso}T00:00:00Z`) / DAY_MS;
const dayToIso = (day) => new Date(Math.floor(day) * DAY_MS).toISOString().slice(0, 10);
const clampIso = (iso) => (iso < MIN_DATE ? MIN_DATE : iso > MAX_DATE ? MAX_DATE : iso);

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(iso) {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

const RAD = Math.PI / 180;

function toVec(lng, lat) {
  const phi = lat * RAD;
  const lam = lng * RAD;
  return [Math.cos(phi) * Math.cos(lam), Math.cos(phi) * Math.sin(lam), Math.sin(phi)];
}

function fromVec([x, y, z]) {
  return [Math.atan2(y, x) / RAD, Math.atan2(z, Math.hypot(x, y)) / RAD];
}

// Point a fraction k of the way along the great circle from a to b
// ([lng, lat] pairs). Tracks follow great circles, as ships and armies
// moving between distant waypoints roughly do.
function slerp(a, b, k) {
  const va = toVec(a[0], a[1]);
  const vb = toVec(b[0], b[1]);
  const dot = Math.min(1, Math.max(-1, va[0] * vb[0] + va[1] * vb[1] + va[2] * vb[2]));
  const omega = Math.acos(dot);
  if (omega < 1e-9) return [a[0], a[1]];
  const s = Math.sin(omega);
  const wa = Math.sin((1 - k) * omega) / s;
  const wb = Math.sin(k * omega) / s;
  return fromVec([wa * va[0] + wb * vb[0], wa * va[1] + wb * vb[1], wa * va[2] + wb * vb[2]]);
}

function angularDistance(a, b) {
  const va = toVec(a[0], a[1]);
  const vb = toVec(b[0], b[1]);
  return Math.acos(Math.min(1, Math.max(-1, va[0] * vb[0] + va[1] * vb[1] + va[2] * vb[2])));
}

// Compass bearing (degrees clockwise from north) from a to b.
function bearing(a, b) {
  const p1 = a[1] * RAD;
  const p2 = b[1] * RAD;
  const dl = (b[0] - a[0]) * RAD;
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return (Math.atan2(y, x) / RAD + 360) % 360;
}

// Shift each longitude by whole turns so consecutive points stay within
// 180° of each other; MapLibre then draws antimeridian crossings as one
// continuous line instead of a stroke across the whole map.
function unwrap(coords) {
  const out = [];
  let prev = null;
  coords.forEach(([lng, lat]) => {
    let x = lng;
    if (prev !== null) {
      while (x - prev > 180) x -= 360;
      while (x - prev < -180) x += 360;
    }
    out.push([x, lat]);
    prev = x;
  });
  return out;
}

// Great-circle polyline through the points, densified every ~2°.
function arc(points) {
  const out = [];
  for (let i = 0; i < points.length; i++) {
    if (i > 0) {
      const a = points[i - 1];
      const b = points[i];
      const n = Math.ceil(angularDistance(a, b) / (2 * RAD));
      for (let j = 1; j < n; j++) out.push(slerp(a, b, j / n));
    }
    out.push(points[i]);
  }
  return unwrap(out);
}

function geometries(geojson) {
  if (!geojson) return [];
  if (geojson.type === "FeatureCollection") return geojson.features.map((f) => f.geometry).filter(Boolean);
  if (geojson.type === "Feature") return [geojson.geometry].filter(Boolean);
  return [geojson];
}

function polygonsOf(geojson) {
  const polys = [];
  geometries(geojson).forEach((g) => {
    if (g.type === "Polygon") polys.push(g.coordinates);
    else if (g.type === "MultiPolygon") g.coordinates.forEach((p) => polys.push(p));
  });
  return polys;
}

// Planar area and centroid of a ring (degrees; fine for label placement).
function ringStats(ring) {
  const pts = unwrap(ring);
  let a = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const f = pts[j][0] * pts[i][1] - pts[i][0] * pts[j][1];
    a += f;
    cx += (pts[j][0] + pts[i][0]) * f;
    cy += (pts[j][1] + pts[i][1]) * f;
  }
  if (Math.abs(a) < 1e-12) return { area: 0, center: pts[0] };
  let lng = cx / (3 * a);
  while (lng > 180) lng -= 360;
  while (lng < -180) lng += 360;
  return { area: Math.abs(a / 2), center: [lng, cy / (3 * a)] };
}

// Label anchor of a boundary: the centroid of its largest polygon, and the
// total area, which ranks labels (bigger countries keep their names).
function labelPoint(geojson) {
  let best = null;
  let total = 0;
  polygonsOf(geojson).forEach((rings) => {
    if (!rings[0] || rings[0].length < 3) return;
    const s = ringStats(rings[0]);
    total += s.area;
    if (!best || s.area > best.area) best = s;
  });
  return best ? { center: best.center, area: total } : null;
}

// ---------------------------------------------------------------------------
// Unit tracks
// ---------------------------------------------------------------------------

// Track points for a unit, in days: one per position, plus a second point
// at end_date for a stay (a siege, a fleet at anchor).
function unitTrack(unit) {
  const pts = [];
  unit.positions.forEach((p) => {
    const at = [p.longitude, p.latitude];
    pts.push({ day: isoToDay(p.date), at, label: p.label });
    if (p.end_date && p.end_date > p.date) pts.push({ day: isoToDay(p.end_date), at, label: p.label });
  });
  pts.sort((a, b) => a.day - b.day);
  // After its last recorded move the unit holds there until the battles it
  // was fighting ended, it was deactivated or it was disbanded.
  const last = pts[pts.length - 1];
  if (last && unit.hold_until && isoToDay(unit.hold_until) > last.day) {
    pts.push({ day: isoToDay(unit.hold_until), at: last.at, label: last.label });
  }
  const gaps = (unit.gaps || []).map(([from, to]) => ({ from: isoToDay(from), to: to ? isoToDay(to) : Infinity }));
  gaps.forEach((g) => {
    const before = pts.filter((p) => p.day <= g.from).pop();
    const after = pts.find((p) => p.day >= g.to);
    if (before && before.day < g.from) pts.push({ day: g.from, at: before.at, label: before.label });
    if (after && after.day > g.to) pts.push({ day: g.to, at: after.at, label: after.label });
  });
  pts.sort((a, b) => a.day - b.day);
  pts.gaps = gaps;
  return pts;
}

// [lng, lat] of a track at a (fractional) day, interpolated along the great
// circle between the waypoints around it; null outside the track's dates
// and in its gaps.
function trackAt(track, day) {
  if (track.length === 0 || day < track[0].day || day > track[track.length - 1].day) return null;
  if ((track.gaps || []).some((g) => day > g.from && day < g.to)) return null;
  for (let i = 1; i < track.length; i++) {
    if (day <= track[i].day) {
      const a = track[i - 1];
      const b = track[i];
      const k = b.day > a.day ? (day - a.day) / (b.day - a.day) : 1;
      return slerp(a.at, b.at, k);
    }
  }
  return track[track.length - 1].at;
}

function unitKindLabel(kind) {
  return UNIT_KIND_LABELS[kind] || kind;
}

// Label of the last position the unit reached on or before iso.
function unitPlaceOn(unit, iso) {
  let label = null;
  unit.positions.forEach((p) => {
    if (p.date <= iso) label = p.label;
  });
  return label;
}

function unitStrengthOn(unit, iso) {
  let figure = null;
  (unit.strengths || []).forEach((f) => {
    if (!f.date || f.date <= iso) figure = f;
  });
  if (!figure) return "";
  const range = figure.high && figure.high !== figure.low ? `${figure.low.toLocaleString()}–${figure.high.toLocaleString()}` : figure.low.toLocaleString();
  return ` · ${range} strong`;
}

// Sub-units and attached ships moving with the unit on the date.
function unitsWithOn(unit, iso) {
  return unit.sub_units.filter((s) => (!s.start_date || s.start_date <= iso) && (!s.end_date || iso <= s.end_date));
}

// ---------------------------------------------------------------------------
// Icons, drawn on canvas at 2x and registered with the map on demand (event
// markers and unit counters are in map_icons.js)
// ---------------------------------------------------------------------------

// Direction of travel: a chevron drawn ahead of a moving unit.
function arrowIcon() {
  return canvasImage(32, 32, (ctx) => {
    ctx.fillStyle = "#ffffff";
    ctx.strokeStyle = "#0b0e14";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(16, 4);
    ctx.lineTo(28, 24);
    ctx.lineTo(16, 18);
    ctx.lineTo(4, 24);
    ctx.closePath();
    ctx.stroke();
    ctx.fill();
  });
}

// A city dot. Tile cities get no capital mark: the tiles know today's
// capitals, which would mislead on a historical date. Our own places know
// when they were capitals and get a ring then.
function cityIcon(capital = false) {
  return canvasImage(16, 16, (ctx) => {
    ctx.fillStyle = "#c3ccdc";
    ctx.strokeStyle = "#0b0e14";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(8, 8, capital ? 3 : 2.5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fill();
    if (capital) {
      ctx.strokeStyle = "#c3ccdc";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(8, 8, 6.5, 0, Math.PI * 2);
      ctx.stroke();
    }
  });
}

// Diagonal hatching for occupied territory.
function hatchPattern() {
  return canvasImage(16, 16, (ctx, s) => {
    ctx.strokeStyle = "rgba(255,255,255,0.55)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let o = -s; o <= s; o += 8) {
      ctx.moveTo(o, s);
      ctx.lineTo(o + s, 0);
    }
    ctx.stroke();
  });
}

// The square drawn around the selected event or unit.
function selectedIcon() {
  return canvasImage(88, 88, (ctx) => {
    ctx.fillStyle = "rgba(255,224,138,0.22)";
    ctx.fillRect(6, 6, 76, 76);
    ctx.strokeStyle = "#ffe08a";
    ctx.lineWidth = 4;
    ctx.strokeRect(6, 6, 76, 76);
  });
}

// ---------------------------------------------------------------------------
// Map style
// ---------------------------------------------------------------------------

const EMPTY = { type: "FeatureCollection", features: [] };

function baseStyle() {
  const geo = () => ({ type: "geojson", data: EMPTY });
  return {
    version: 8,
    glyphs: GLYPHS_URL,
    sources: {
      omt: { type: "vector", url: TILES_URL },
      land: geo(),
      boundaries: { type: "geojson", data: EMPTY },
      "boundary-labels": geo(),
      fronts: geo(),
      places: geo(),
      events: geo(),
      "unit-tracks": geo(),
      "unit-trails": geo(),
      units: geo(),
    },
    layers: [
      { id: "background", type: "background", paint: { "background-color": COLORS.land } },
      // fallback land (only filled in when the tiles fail)
      { id: "ne-land", type: "fill", source: "land", paint: { "fill-color": COLORS.land } },
      {
        id: "landcover",
        type: "fill",
        source: "omt",
        "source-layer": "landcover",
        filter: ["in", ["get", "class"], ["literal", ["wood", "forest", "grass", "sand", "ice", "glacier"]]],
        paint: {
          "fill-color": ["match", ["get", "class"], ["ice", "glacier"], COLORS.ice, COLORS.landcover],
          "fill-opacity": 0.6,
        },
      },
      { id: "water", type: "fill", source: "omt", "source-layer": "water", paint: { "fill-color": COLORS.water } },
      {
        id: "waterway",
        type: "line",
        source: "omt",
        "source-layer": "waterway",
        minzoom: 4,
        filter: ["in", ["get", "class"], ["literal", ["river", "canal"]]],
        paint: { "line-color": COLORS.waterLine, "line-width": ["interpolate", ["linear"], ["zoom"], 4, 0.4, 10, 2] },
      },
      // polities; claimed land gets an invisible fill so a click there finds
      // its de jure owner (Poland under occupation, 1939-45)
      {
        id: "country-claimed-fill",
        type: "fill",
        source: "boundaries",
        filter: ["!=", ["get", "claim"], "de_facto"],
        paint: { "fill-color": "#000", "fill-opacity": 0 },
      },
      {
        id: "country-fill",
        type: "fill",
        source: "boundaries",
        filter: ["all", ["==", ["get", "claim"], "de_facto"], ["!=", ["get", "control"], "occupied"]],
        paint: { "fill-color": ["get", "color"], "fill-opacity": ["case", ["get", "approx"], 0.08, 0.16] },
      },
      {
        id: "country-occupied-tint",
        type: "fill",
        source: "boundaries",
        filter: ["==", ["get", "control"], "occupied"],
        paint: { "fill-color": ["get", "color"], "fill-opacity": 0.3 },
      },
      {
        id: "country-occupied",
        type: "fill",
        source: "boundaries",
        filter: ["==", ["get", "control"], "occupied"],
        paint: { "fill-pattern": "hatch", "fill-opacity": 0.45 },
      },
      {
        id: "country-selected-fill",
        type: "fill",
        source: "boundaries",
        filter: ["==", ["get", "entity_id"], -1],
        paint: { "fill-color": "#ffe08a", "fill-opacity": 0.18 },
      },
      {
        id: "country-line",
        type: "line",
        source: "boundaries",
        filter: ["all", ["==", ["get", "claim"], "de_facto"], ["!", ["get", "approx"]]],
        paint: {
          "line-color": ["match", ["get", "control"], ...Object.entries(CONTROL_COLORS).flat(), CONTROL_COLORS.sovereign],
          "line-width": ["interpolate", ["linear"], ["zoom"], 1, 0.6, 6, 1.4],
          "line-opacity": 0.9,
        },
      },
      // approximate shapes (traced from descriptions): lighter fill, dashed edge
      {
        id: "country-line-approx",
        type: "line",
        source: "boundaries",
        filter: ["all", ["==", ["get", "claim"], "de_facto"], ["get", "approx"]],
        paint: {
          "line-color": ["match", ["get", "control"], ...Object.entries(CONTROL_COLORS).flat(), CONTROL_COLORS.sovereign],
          "line-width": ["interpolate", ["linear"], ["zoom"], 1, 0.6, 6, 1.4],
          "line-opacity": 0.7,
          "line-dasharray": [2, 2],
        },
      },
      {
        id: "country-claimed",
        type: "line",
        source: "boundaries",
        filter: ["!=", ["get", "claim"], "de_facto"],
        paint: { "line-color": "#a08c5b", "line-width": 1.2, "line-dasharray": [3, 2] },
      },
      {
        id: "country-hover",
        type: "line",
        source: "boundaries",
        filter: ["==", ["get", "entity_id"], -1],
        paint: { "line-color": "#c8d3ea", "line-width": 1.6 },
      },
      {
        id: "country-selected-line",
        type: "line",
        source: "boundaries",
        filter: ["==", ["get", "entity_id"], -1],
        paint: { "line-color": "#ffe08a", "line-width": 2.6 },
      },
      // Front lines: a bold double line, each half in its side's color, the
      // left side's half offset to the left of the line as drawn.
      {
        id: "front-left",
        type: "line",
        source: "fronts",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          "line-color": ["get", "left_color"],
          "line-width": ["interpolate", ["linear"], ["zoom"], 2, 1.5, 8, 4],
          "line-offset": ["interpolate", ["linear"], ["zoom"], 2, -0.75, 8, -2],
        },
      },
      {
        id: "front-right",
        type: "line",
        source: "fronts",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          "line-color": ["get", "right_color"],
          "line-width": ["interpolate", ["linear"], ["zoom"], 2, 1.5, 8, 4],
          "line-offset": ["interpolate", ["linear"], ["zoom"], 2, 0.75, 8, 2],
        },
      },
      // the selected unit's whole route, under the markers
      {
        id: "unit-tracks",
        type: "line",
        source: "unit-tracks",
        filter: ["==", ["get", "id"], -1],
        layout: { "line-cap": "round" },
        paint: { "line-color": ["get", "color"], "line-width": 1.2, "line-opacity": 0.35, "line-dasharray": [2, 2] },
      },
      {
        id: "unit-trails",
        type: "line",
        source: "unit-trails",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": ["get", "color"],
          "line-width": ["interpolate", ["linear"], ["zoom"], 1, 2, 8, 4],
          "line-opacity": ["get", "opacity"],
        },
      },
      // Cities, today's names from the tiles. They sit below events, units
      // and country names, so they are the first labels hidden when space
      // is tight. National capitals show from the world view, regional
      // capitals and the largest cities as the map zooms in, towns last.
      {
        id: "place-label",
        type: "symbol",
        source: "omt",
        "source-layer": "place",
        minzoom: 1.5,
        filter: [
          "step", ["zoom"],
          ["==", ["get", "capital"], 2],
          4, ["any", ["==", ["get", "capital"], 2], ["all", ["==", ["get", "class"], "city"], ["<=", ["coalesce", ["get", "rank"], 99], 4]]],
          5, ["any", ["has", "capital"], ["==", ["get", "class"], "city"]],
          7, ["any", ["has", "capital"], ["in", ["get", "class"], ["literal", ["city", "town"]]]],
        ],
        layout: {
          "icon-image": "city",
          "text-field": ["coalesce", ["get", "name:en"], ["get", "name"]],
          "text-font": FONT,
          "text-size": ["interpolate", ["linear"], ["zoom"], 2, ["case", ["==", ["get", "capital"], 2], 10, 9], 10, ["case", ["has", "capital"], 14, 12]],
          "text-variable-anchor": ["left", "right", "top", "bottom"],
          "text-radial-offset": 0.5,
          "text-justify": "auto",
          "text-max-width": 7,
          "symbol-sort-key": ["+", ["*", 10, ["coalesce", ["get", "capital"], 9]], ["coalesce", ["get", "rank"], 99]],
        },
        paint: {
          "text-color": ["case", ["has", "capital"], "#a9b4c8", "#7d889e"],
          "text-halo-color": COLORS.halo,
          "text-halo-width": 1.2,
        },
      },
      {
        id: "country-label",
        type: "symbol",
        source: "boundary-labels",
        layout: {
          "text-field": ["get", "name"],
          "text-font": FONT,
          "text-transform": "uppercase",
          "text-letter-spacing": 0.08,
          "text-max-width": 8,
          "text-size": ["interpolate", ["linear"], ["get", "area"], 1, 9, 50, 11, 500, 14, 3000, 17],
          "symbol-sort-key": ["-", 0, ["get", "area"]],
        },
        paint: { "text-color": "#aab6cf", "text-halo-color": COLORS.halo, "text-halo-width": 1.4 },
      },
      // Our own places, under the name they had on the date. They outrank
      // the tile cities (one at the same spot is hidden) but not events.
      {
        id: "history-place",
        type: "symbol",
        source: "places",
        minzoom: 2,
        filter: ["any", ["get", "capital"], [">=", ["zoom"], 4]],
        layout: {
          "icon-image": ["case", ["get", "capital"], "capital", "city"],
          "text-field": ["get", "name"],
          "text-font": FONT,
          "text-size": ["interpolate", ["linear"], ["zoom"], 2, ["case", ["get", "capital"], 11, 10], 10, ["case", ["get", "capital"], 15, 13]],
          "text-variable-anchor": ["left", "right", "top", "bottom"],
          "text-radial-offset": 0.6,
          "text-justify": "auto",
          "text-max-width": 7,
          "symbol-sort-key": ["case", ["get", "capital"], 0, 1],
        },
        paint: { "text-color": "#d5dceb", "text-halo-color": COLORS.halo, "text-halo-width": 1.3 },
      },
      // events: a marker by icon and state (under way, over, to come);
      // battles under way pulse
      {
        id: "event-pulse",
        type: "symbol",
        source: "events",
        filter: ["get", "pulse"],
        layout: { "icon-image": "pulse", "icon-allow-overlap": true, "icon-ignore-placement": true },
      },
      {
        id: "event-selected",
        type: "symbol",
        source: "events",
        filter: ["==", ["get", "id"], -1],
        layout: { "icon-image": "selected", "icon-size": 0.6, "icon-allow-overlap": true, "icon-ignore-placement": true },
      },
      {
        id: "event-icon",
        type: "symbol",
        source: "events",
        layout: {
          "icon-image": ["get", "image"],
          "icon-size": ["interpolate", ["linear"], ["get", "importance"], 1, 0.65, 6, 1],
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "symbol-sort-key": ["get", "order"],
        },
      },
      {
        id: "event-label",
        type: "symbol",
        source: "events",
        layout: {
          "text-field": ["get", "name"],
          "text-font": FONT,
          "text-size": ["interpolate", ["linear"], ["get", "importance"], 1, 11, 6, 13],
          "text-max-width": 12,
          "text-variable-anchor": ["left", "right", "top", "bottom", "top-left", "bottom-right"],
          "text-radial-offset": 1.2,
          "symbol-sort-key": ["-", 0, ["get", "order"]],
        },
        paint: {
          "text-color": ["case", ["get", "now"], COLORS.text, COLORS.muted],
          "text-halo-color": COLORS.halo,
          "text-halo-width": 1.5,
        },
      },
      // units
      {
        id: "unit-halo",
        type: "symbol",
        source: "units",
        filter: ["==", ["get", "id"], -1],
        layout: { "icon-image": "selected", "icon-size": 0.85, "icon-allow-overlap": true, "icon-ignore-placement": true },
      },
      {
        id: "unit-arrow",
        type: "symbol",
        source: "units",
        filter: ["get", "moving"],
        layout: {
          "icon-image": "arrow",
          "icon-size": 0.5,
          "icon-rotate": ["get", "bearing"],
          "icon-rotation-alignment": "map",
          "icon-offset": [0, -60],
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        },
      },
      {
        id: "unit-icon",
        type: "symbol",
        source: "units",
        layout: {
          "icon-image": ["get", "image"],
          "icon-size": ["interpolate", ["linear"], ["get", "rank"], 3, 0.8, 9, 1.15],
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "symbol-sort-key": ["-", 0, ["get", "rank"]],
        },
      },
      {
        id: "unit-label",
        type: "symbol",
        source: "units",
        layout: {
          "text-field": ["get", "name"],
          "text-font": FONT,
          "text-size": ["interpolate", ["linear"], ["get", "rank"], 3, 10, 9, 12],
          "text-max-width": 10,
          "text-variable-anchor": ["top", "bottom", "left", "right"],
          "text-radial-offset": 1.6,
          "symbol-sort-key": ["-", 0, ["get", "rank"]],
        },
        paint: { "text-color": ["get", "color"], "text-halo-color": COLORS.halo, "text-halo-width": 1.5 },
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// Feature builders
// ---------------------------------------------------------------------------

function polityColor(entityId) {
  return POLITY_PALETTE[Math.abs(entityId) % POLITY_PALETTE.length];
}

function boundaryFeatures(boundaries) {
  return {
    type: "FeatureCollection",
    features: boundaries.flatMap((b) =>
      geometries(b.geojson).map((geometry) => ({
        type: "Feature",
        geometry,
        properties: {
          bid: b.id,
          entity_id: b.entity_id,
          name: b.entity_name,
          claim: b.claim_kind,
          control: b.control_kind,
          source_url: b.source_url || "",
          approx: !!b.approximate,
          color: polityColor(b.entity_id),
        },
      }))
    ),
  };
}

// Each side of the wars on screen gets one force color, shared by its units
// and its half of every front line. A side is its label in the war
// ("Allies", "Axis"), so allies share a color; a state with no recorded
// side is a side of its own. Well-known sides keep their usual color
// (side_colors.js: the Union in blue). The other sides take the force
// colors no usual side on screen has, most units and fronts first.
const sideKey = (label, entityId) => (label ? `side:${label}` : entityId != null ? `state:${entityId}` : null);

function forceColors(units, fronts) {
  const counts = new Map();
  const taken = new Set(); // usual colors on screen
  const ofEntity = new Map(); // a state's side label, for battles and cards
  const count = (label, entityId) => {
    const key = sideKey(label, entityId);
    if (!key) return;
    if (entityId != null && !ofEntity.has(entityId)) ofEntity.set(entityId, label);
    const usual = usualSideColor(label);
    if (usual) taken.add(usual);
    else counts.set(key, (counts.get(key) || 0) + 1);
  };
  units.forEach((u) => count(u.side, u.entity_id));
  fronts.forEach((f) => {
    count(f.left_side, f.left_entity?.id);
    count(f.right_side, f.right_entity?.id);
  });
  const free = FORCE_PALETTE.filter((c) => !taken.has(c));
  const palette = free.length ? free : FORCE_PALETTE;
  const order = [...counts.keys()].sort((a, b) => counts.get(b) - counts.get(a) || (a < b ? -1 : 1));
  const color = (label, entityId) => {
    const usual = usualSideColor(label);
    if (usual) return usual;
    const i = order.indexOf(sideKey(label, entityId));
    return i >= 0 ? palette[i % palette.length] : FORCE_NO_ENTITY;
  };
  return {
    side: color,
    // A state's color: that of its side in a war on screen, else of the
    // given side label.
    entity: (entityId, label) => color(ofEntity.has(entityId) ? ofEntity.get(entityId) : label, entityId),
  };
}

function frontFeatures(fronts, colors) {
  return {
    type: "FeatureCollection",
    features: fronts.flatMap((f) =>
      geometries(f.geojson).map((geometry) => ({
        type: "Feature",
        geometry,
        properties: {
          id: f.id,
          left_color: colors.side(f.left_side, f.left_entity?.id),
          right_color: colors.side(f.right_side, f.right_entity?.id),
        },
      }))
    ),
  };
}

function boundaryLabelFeatures(boundaries) {
  const byEntity = new Map();
  boundaries
    .filter((b) => b.claim_kind === "de_facto" && b.control_kind !== "occupied")
    .forEach((b) => {
      const p = labelPoint(b.geojson);
      if (!p) return;
      const prev = byEntity.get(b.entity_id);
      if (!prev || p.area > prev.area) byEntity.set(b.entity_id, { ...p, name: b.entity_name });
    });
  return {
    type: "FeatureCollection",
    features: [...byEntity.entries()].map(([entityId, p]) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: p.center },
      properties: { entity_id: entityId, name: p.name, area: p.area },
    })),
  };
}

// ---------------------------------------------------------------------------
// Places
// ---------------------------------------------------------------------------

// Touch screens get finger-sized hit areas and controls.
const COARSE = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;
const NARROW_QUERY = "(max-width: 640px)";

const covers = (period, iso) => (!period.start_date || period.start_date <= iso) && (!period.end_date || iso <= period.end_date);

function placeNameOn(place, iso) {
  const names = place.names.filter((n) => covers(n, iso));
  return names.length ? names[names.length - 1].name : place.name;
}

function placeCapitalOf(place, iso) {
  return place.capitals.filter((c) => covers(c, iso)).map((c) => c.entity_id);
}

function placeFeatures(places, iso) {
  return {
    type: "FeatureCollection",
    features: places
      .filter((p) => p.kind !== "region")
      .map((p) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.longitude, p.latitude] },
        properties: { id: p.id, name: placeNameOn(p, iso), capital: placeCapitalOf(p, iso).length > 0 },
      })),
  };
}

// entity id -> [lng, lat] of its capital on the date.
function capitalsOn(places, iso) {
  const found = new Map();
  places.forEach((p) => placeCapitalOf(p, iso).forEach((id) => found.has(id) || found.set(id, [p.longitude, p.latitude])));
  return found;
}

function eventImportance(ev) {
  return (ICON_WEIGHT[ev.icon] || 1) + Math.log2(1 + (ev.people_count || 0));
}

// Image id of a unit's counter: a NATO frame for land units, a silhouette
// facing its way of travel for ships and aircraft.
function unitImage(unit, color, heading) {
  if (unit.branch === "land") return ["land", unit.unit_type || "", unit.kind, color].join("|");
  const year = parseInt((unit.positions[0]?.date || unit.hold_until || "1900").slice(0, 4), 10);
  const shape = unit.branch === "air" ? "airplane" : navalShape(unit.unit_type, year);
  return ["craft", shape, color, heading > 180 ? "w" : "e"].join("|");
}

// "now" while the event is under way on the date, else "past" or "next".
function eventState(ev, iso) {
  if (ev.start_date > iso) return "next";
  return (ev.end_date || ev.start_date) < iso ? "past" : "now";
}

// Colors of a battle's sides: a side takes the color of its first state's
// side in a war on screen, else that of its own label, else that state's
// polity color, and never the color of a side before it.
function sideColors(ev, colors) {
  const out = [];
  (ev.sides || []).forEach(({ side, entity_ids: ids }) => {
    let c = ids.map((id) => colors.entity(id, side)).find((x) => x !== FORCE_NO_ENTITY) || polityColor(ids[0]);
    for (let i = 0; out.includes(c) && i < FORCE_PALETTE.length; i++) c = FORCE_PALETTE[i];
    out.push(c);
  });
  return out.length > 1 ? out.slice(0, 4) : [];
}

// An event that names only a country is drawn at the country's capital on
// the event's first day (a coup, a revolution), else at the country's
// label point. Events under way draw over those that are over or to come.
function eventFeatures(events, labelPoints, places, iso, colors) {
  const features = [];
  events.forEach((ev) => {
    const state = eventState(ev, iso);
    const color = ICON_COLORS[ev.icon] || DEFAULT_EVENT_COLOR;
    const battle = ev.icon === "sword" && state === "now";
    const sides = battle ? sideColors(ev, colors) : [];
    const importance = eventImportance(ev);
    // A place beats a whole country: an event at St Petersburg that also
    // names Russia is drawn once, at St Petersburg. Only the first marker
    // of an event carries its name, so labels are not repeated.
    const locations = ev.locations || [];
    const points = locations.filter((loc) => loc.kind !== "entity");
    let labelled = false;
    (points.length ? points : locations).forEach((loc, i) => {
      let coords = null;
      if (loc.kind === "entity") {
        coords = capitalsOn(places, ev.start_date).get(loc.entity_id) || labelPoints.get(loc.entity_id) || null;
      } else if (loc.latitude != null && loc.longitude != null) {
        coords = [loc.longitude, loc.latitude];
      }
      if (!coords) return;
      const first = !labelled;
      labelled = true;
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: coords },
        properties: {
          id: ev.id,
          key: `${ev.id}-${i}`,
          name: first ? ev.name : "",
          image: ["ev", ev.icon || "", color, state, sides.join(",")].join("|"),
          importance,
          order: (state === "now" ? 100 : 0) + importance,
          now: state === "now",
          pulse: battle,
        },
      });
    });
  });
  return { type: "FeatureCollection", features };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Globe() {
  const mountRef = useRef(null);
  const params = new URLSearchParams(window.location.search);
  const [at, setAt] = useState(() => {
    // Deep-link support for battle/war pages: ?at=YYYY-MM-DD
    const m = params.get("at");
    return /^\d{4}-\d{2}-\d{2}$/.test(m || "") ? clampIso(m) : todayIso();
  });
  const [windowDays, setWindowDays] = useState(365);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(10);
  const [showForces, setShowForces] = useState(true);
  const [selection, setSelection] = useState(null); // {type: event|country|unit, ...}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW_QUERY).matches);
  const [mapTop, setMapTop] = useState(170);
  const [eras, setEras] = useState([]); // eras of the day for the countries in view
  const [erasOpen, setErasOpen] = useState(() => !window.matchMedia(NARROW_QUERY).matches);

  const mapRef = useRef(null);
  const readyRef = useRef(false);
  const dayRef = useRef(isoToDay(at) + 0.5); // continuous time, in days
  const bufferRef = useRef(null); // {from, to, events, boundaries, units}
  const pendingRef = useRef(null); // range being fetched
  const deepLinkEraRef = useRef(parseInt(params.get("era"), 10) || null); // era pages link with ?era=id
  const unitsRef = useRef([]); // [{unit, track, color}]
  const signaturesRef = useRef({});
  const optsRef = useRef({});
  optsRef.current = { at, windowDays, showForces, speed, playing };
  const selectionRef = useRef(null);
  selectionRef.current = selection;
  const entityCacheRef = useRef({});
  const eventCacheRef = useRef({});
  const [eventInfo, setEventInfo] = useState(null);
  const placesRef = useRef([]);
  const shapesRef = useRef(new Map()); // boundary id -> {version, geojson}

  // ---- data buffering ------------------------------------------------------
  const fetchJson = async (url) => {
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error(`request failed: ${res.status}`);
    return res.json();
  };

  // Border shapes are most of the map's data and the same snapshots come back
  // in every buffer, so each one is fetched once and kept for the visit.
  const withShapes = async (boundaries) => {
    const shapes = shapesRef.current;
    const missing = boundaries.filter((b) => shapes.get(b.id)?.version !== b.version);
    const batches = [];
    for (let i = 0; i < missing.length; i += SHAPES_PER_REQUEST) batches.push(missing.slice(i, i + SHAPES_PER_REQUEST));
    await Promise.all(batches.map(async (batch) => {
      const ids = batch.map((b) => b.id).sort((a, b) => a - b);
      const v = Math.max(...batch.map((b) => b.version));
      const geojson = await fetchJson(`/globe/boundary_shapes?ids=${ids.join(",")}&v=${v}`);
      batch.forEach((b) => shapes.set(b.id, { version: b.version, geojson: geojson[b.id] }));
    }));
    return boundaries.map((b) => ({ ...b, geojson: shapes.get(b.id).geojson }));
  };

  // The days the map needs data for: the event window around the date.
  const neededRange = (iso, win) => [isoToDay(iso) - win, isoToDay(iso) + win];

  // Load a buffer covering the needed range with room ahead (playback runs
  // forward), unless one is loaded or loading already.
  const ensureBuffer = async (iso, win) => {
    const [needFrom, needTo] = neededRange(iso, win);
    const buf = bufferRef.current;
    const covered = buf && buf.from <= needFrom && buf.to >= needTo;
    const nearEnd = buf && buf.to - needTo < 120;
    if (covered && !nearEnd) return;
    const from = needFrom - 90;
    const to = needTo + Math.max(365, win);
    const pending = pendingRef.current;
    if (pending && pending.from <= needFrom && pending.to >= needTo) return;
    pendingRef.current = { from, to };
    if (!covered) setLoading(true);
    const range = `from=${dayToIso(from)}&to=${dayToIso(to)}`;
    try {
      const [events, boundaries, units, fronts] = await Promise.all([
        fetchJson(`/globe/events?${range}`),
        fetchJson(`/globe/boundaries?${range}`).then(withShapes),
        fetchJson(`/globe/units?${range}`),
        fetchJson(`/globe/fronts?${range}`),
      ]);
      if (pendingRef.current?.from !== from || pendingRef.current?.to !== to) return; // superseded
      bufferRef.current = { from, to, events, boundaries, units, fronts };
      const eraLink = deepLinkEraRef.current && events.find((ev) => ev.era && ev.id === deepLinkEraRef.current);
      if (eraLink) {
        deepLinkEraRef.current = null;
        setSelection({ type: "event", event: eraLink });
      }
      signaturesRef.current = {};
      setError(null);
      renderData();
    } catch (e) {
      setError(e.message);
    } finally {
      if (pendingRef.current?.from === from && pendingRef.current?.to === to) {
        pendingRef.current = null;
        setLoading(false);
      }
    }
  };

  // ---- drawing the buffered data for the current day ------------------------
  const setSource = (id, signature, build) => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    if (signaturesRef.current[id] === signature) return;
    signaturesRef.current[id] = signature;
    map.getSource(id)?.setData(build());
  };

  const labelPointsRef = useRef(new Map());
  const activeBoundariesRef = useRef([]);
  const forceColorRef = useRef(forceColors([], []));
  const pulsingRef = useRef(false); // a battle under way is on the map

  const renderData = () => {
    const buf = bufferRef.current;
    if (!buf || !readyRef.current) return;
    const { at: iso, windowDays: win, showForces: forces } = optsRef.current;

    const active = buf.boundaries.filter(
      (b) => (!b.start_date || b.start_date <= iso) && (!b.end_date || iso <= b.end_date)
    );
    activeBoundariesRef.current = active;
    const boundarySig = `${buf.from}:${active.map((b) => b.id).join(",")}`;
    if (signaturesRef.current.boundaries !== boundarySig) {
      const labels = boundaryLabelFeatures(active);
      labelPointsRef.current = new Map(labels.features.map((f) => [f.properties.entity_id, f.geometry.coordinates]));
      setSource("boundaries", boundarySig, () => boundaryFeatures(active));
      setSource("boundary-labels", boundarySig, () => labels);
    }

    const fronts = buf.fronts.filter((f) => f.start_date <= iso && (!f.end_date || iso <= f.end_date));
    const colors = forceColors(buf.units, buf.fronts);
    forceColorRef.current = colors;
    setSource("fronts", `${buf.from}:${fronts.map((f) => f.id).join(",")}`, () => frontFeatures(fronts, colors));

    const [from, to] = neededRange(iso, win);
    const events = buf.events.filter((ev) => {
      if (ev.era) return false; // listed in the Eras panel, not drawn
      const s = isoToDay(ev.start_date);
      const e = ev.end_date ? isoToDay(ev.end_date) : s;
      return s <= to && e >= from;
    });
    const places = placesRef.current;
    const eventSig = events.map((e) => `${e.id}${eventState(e, iso)[0]}`).join(",");
    pulsingRef.current = events.some((ev) => ev.icon === "sword" && eventState(ev, iso) === "now");
    setSource("events", `${boundarySig}|${places.length}|${eventSig}`, () =>
      eventFeatures(events, labelPointsRef.current, places, iso, colors)
    );
    const placeData = placeFeatures(places, iso);
    setSource("places", JSON.stringify(placeData.features.map((f) => [f.properties.name, f.properties.capital])), () => placeData);

    // units: tracks change only with the buffer; positions every frame
    const unitSig = `${buf.from}:${forces}`;
    if (signaturesRef.current.unitSet !== unitSig) {
      signaturesRef.current.unitSet = unitSig;
      const list = forces ? buf.units : [];
      unitsRef.current = list.map((u) => ({ unit: u, track: unitTrack(u), color: colors.side(u.side, u.entity_id) }));
      setSource("unit-tracks", unitSig, () => ({
        type: "FeatureCollection",
        features: unitsRef.current
          .filter((u) => u.track.length > 1)
          .map((u) => ({
            type: "Feature",
            geometry: { type: "LineString", coordinates: arc(u.track.map((p) => p.at)) },
            properties: { id: u.unit.id, color: u.color },
          })),
      }));
    }
    renderUnits(dayRef.current);
    updateEras();
  };

  // The Eras panel follows the date and the view.
  const updateEras = () => {
    const map = mapRef.current;
    const buf = bufferRef.current;
    if (!map || !buf) return;
    const found = erasInView(map, buf.events, activeBoundariesRef.current, optsRef.current.at);
    setEras((old) => (old.map((e) => e.id).join() === found.map((e) => e.id).join() ? old : found));
  };

  // Unit icons, direction arrows and fading trails at a fractional day.
  const renderUnits = (day) => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    const points = [];
    const trails = [];
    unitsRef.current.forEach(({ unit, track, color }) => {
      const pos = trackAt(track, day);
      if (!pos) return;
      const before = trackAt(track, day - 1) || pos;
      const ahead = trackAt(track, day + 1) || pos;
      const moving = angularDistance(before, pos) > 1e-5 || angularDistance(pos, ahead) > 1e-5;
      const heading = angularDistance(pos, ahead) > 1e-6 ? bearing(pos, ahead) : bearing(before, pos);
      points.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: pos },
        properties: {
          id: unit.id,
          name: unit.name,
          color,
          image: unitImage(unit, color, heading),
          rank: UNIT_RANK[unit.kind] || 4,
          bearing: heading,
          moving,
        },
      });
      if (!moving) return;
      let prev = pos;
      for (let s = 1; s <= TRAIL_STEPS; s++) {
        const p = trackAt(track, day - (TRAIL_DAYS * s) / TRAIL_STEPS);
        if (!p) break;
        if (angularDistance(prev, p) > 1e-6) {
          trails.push({
            type: "Feature",
            geometry: { type: "LineString", coordinates: arc([prev, p]) },
            properties: { color, opacity: 0.85 * (1 - (s - 1) / TRAIL_STEPS) },
          });
        }
        prev = p;
      }
    });
    map.getSource("units")?.setData({ type: "FeatureCollection", features: points });
    map.getSource("unit-trails")?.setData({ type: "FeatureCollection", features: trails });
  };

  // ---- selection ----------------------------------------------------------
  const applySelectionFilters = (sel) => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    // A country, or every country of a selected era.
    const entityIds = sel?.type === "country" ? [sel.entity.id] : sel?.type === "event" && sel.event.era ? eraRegionIds(sel.event) : [];
    const inSelection = ["in", ["get", "entity_id"], ["literal", entityIds]];
    map.setFilter("country-selected-fill", inSelection);
    map.setFilter("country-selected-line", inSelection);
    map.setFilter("event-selected", ["==", ["get", "id"], sel?.type === "event" ? sel.event.id : -1]);
    map.setFilter("unit-halo", ["==", ["get", "id"], sel?.type === "unit" ? sel.unit.id : -1]);
    map.setFilter("unit-tracks", ["==", ["get", "id"], sel?.type === "unit" ? sel.unit.id : -1]);
  };
  useEffect(() => applySelectionFilters(selection), [selection]);

  const selectedEventId = selection?.type === "event" ? selection.event.id : null;
  useEffect(() => {
    if (!selectedEventId) return;
    let current = true;
    const cached = eventCacheRef.current[selectedEventId];
    (cached ? Promise.resolve(cached) : fetchJson(`/globe/event/${selectedEventId}`))
      .then((data) => {
        eventCacheRef.current[selectedEventId] = data;
        if (current) setEventInfo(data);
      })
      .catch(() => {});
    return () => {
      current = false;
    };
  }, [selectedEventId]);

  const openCountry = async (entityId, occupation, border = null) => {
    const iso = optsRef.current.at;
    const key = `${entityId}|${iso}`;
    try {
      const data = entityCacheRef.current[key] || (await fetchJson(`/globe/entity/${entityId}?at=${iso}`));
      entityCacheRef.current[key] = data;
      setSelection({ type: "country", entity: data.entity, rulers: data.rulers, occupation, border });
    } catch (e) {
      setError(e.message);
    }
  };

  // ---- map (mounted once) --------------------------------------------------
  // On phones the card becomes a bottom sheet and the extra controls fold
  // away; the map fills the screen below the controls.
  useEffect(() => {
    const mq = window.matchMedia(NARROW_QUERY);
    const onChange = () => setNarrow(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const measure = () => setMapTop(Math.round(mountRef.current.getBoundingClientRect().top + window.scrollY));
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [narrow]);

  // MapLibre is most of the page's script, so it loads in its own chunk
  // after the page's controls have rendered.
  useEffect(() => {
    let map = null;
    let cancelled = false;
    import("maplibre-gl").then((maplibregl) => {
      if (!cancelled) map = createMap(maplibregl);
    });
    return () => {
      cancelled = true;
      readyRef.current = false;
      map?.remove();
      mapRef.current = null;
    };
  }, []);

  // The map and its handlers.
  const createMap = (maplibregl) => {
    const workerUrl = mountRef.current.closest("[data-worker-url]")?.dataset.workerUrl;
    if (workerUrl) maplibregl.setWorkerUrl(workerUrl);

    const lat = parseFloat(params.get("lat"));
    const lng = parseFloat(params.get("lng"));
    const deepLink = Number.isFinite(lat) && Number.isFinite(lng);
    const map = new maplibregl.Map({
      container: mountRef.current,
      style: baseStyle(),
      center: deepLink ? [lng, lat] : [30, 30],
      zoom: deepLink ? 5.5 : 1.6,
      minZoom: 1,
      maxZoom: 14,
      attributionControl: {
        compact: true,
        customAttribution:
          'Events: History · Icons: <a href="https://game-icons.net/">game-icons.net</a> (CC BY 3.0) · Borders: <a href="https://www.openhistoricalmap.org/">OpenHistoricalMap</a>, <a href="https://www.naturalearthdata.com/">Natural Earth</a>',
      },
      dragRotate: false,
      pitchWithRotate: false,
      fadeDuration: 0, // labels appear at once: fading them in redraws the map while tiles load
    });
    map.touchZoomRotate.disableRotation();
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new maplibregl.ScaleControl({ unit: "metric" }), "bottom-left");
    // Compact attribution starts open and covers the bottom of a phone map.
    if (window.matchMedia(NARROW_QUERY).matches) {
      mountRef.current.querySelector(".maplibregl-compact-show")?.classList.remove("maplibregl-compact-show");
    }
    mapRef.current = map;

    // Icons are drawn on demand: tiles can ask for them before "load".
    // Unit icons are one per branch and force color.
    const staticImages = {
      arrow: arrowIcon,
      hatch: hatchPattern,
      city: () => cityIcon(),
      capital: () => cityIcon(true),
      selected: selectedIcon,
      pulse: () => pulseFrame(0, PULSE_FRAMES),
    };
    const images = {
      ev: (icon, color, state, sides) => eventMarker(icon, color, state, sides ? sides.split(",") : []),
      land: (type, kind, color) => landCounter(type, kind, color),
      craft: (shape, color, facing) => craftCounter(shape, color, facing === "w"),
    };
    map.setMissingStyleImageResolver((id) => {
      if (map.hasImage(id)) return;
      if (staticImages[id]) return map.addImage(id, staticImages[id](), { pixelRatio: 2 });
      const [kind, ...args] = id.split("|");
      if (images[kind]) map.addImage(id, images[kind](...args), { pixelRatio: 2 });
    });

    // Places change rarely and are few: loaded once, whole.
    fetchJson("/globe/places")
      .then((places) => {
        placesRef.current = places;
        signaturesRef.current = {};
        renderData();
      })
      .catch(() => {});

    map.on("moveend", () => updateEras());

    // The ring around battles under way grows and fades a few times a
    // second, only while one is on the map and the page is visible.
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const frames = Array.from({ length: PULSE_FRAMES }, (_, k) => pulseFrame(k, PULSE_FRAMES));
      let frame = 0;
      const timer = setInterval(() => {
        if (document.hidden || !pulsingRef.current || !map.hasImage("pulse")) return;
        frame = (frame + 1) % PULSE_FRAMES;
        map.updateImage("pulse", frames[frame]);
      }, PULSE_MS);
      map.on("remove", () => clearInterval(timer));
    }

    map.on("load", () => {
      readyRef.current = true;
      renderData();
      applySelectionFilters(selectionRef.current);
    });

    // If the tile server is unreachable, fall back to Natural Earth land.
    let fellBack = false;
    map.on("error", (e) => {
      if (fellBack || e.sourceId !== "omt") return;
      fellBack = true;
      map.setPaintProperty("background", "background-color", COLORS.water);
      fetchJson("/globe/land")
        .then((fc) => map.getSource("land")?.setData(fc))
        .catch(() => {});
    });

    const UNIT_LAYERS = ["unit-icon", "unit-label"];
    const EVENT_LAYERS = ["event-icon", "event-label"];
    const COUNTRY_LAYERS = ["country-occupied", "country-occupied-tint", "country-fill", "country-claimed-fill"];
    const FRONT_LAYERS = ["front-left", "front-right"];
    const near = (point, r) => {
      const d = COARSE ? r * 2.5 : r;
      return [
        [point.x - d, point.y - d],
        [point.x + d, point.y + d],
      ];
    };

    // Click priority: units, then events, then the polity underneath.
    map.on("click", (e) => {
      const unitHit = map.queryRenderedFeatures(near(e.point, 8), { layers: UNIT_LAYERS })[0];
      if (unitHit) {
        const entry = unitsRef.current.find((u) => u.unit.id === unitHit.properties.id);
        if (entry) setSelection({ type: "unit", unit: entry.unit, color: entry.color });
        return;
      }
      const eventHit = map.queryRenderedFeatures(near(e.point, 6), { layers: EVENT_LAYERS })[0];
      if (eventHit) {
        const ev = bufferRef.current?.events.find((x) => x.id === eventHit.properties.id);
        if (ev) {
          setSelection({ type: "event", event: ev });
          map.easeTo({ center: eventHit.geometry.coordinates, zoom: Math.max(map.getZoom(), 4.5), duration: 700 });
        }
        return;
      }
      const placeHit = map.queryRenderedFeatures(near(e.point, 6), { layers: ["history-place"] })[0];
      if (placeHit) {
        const place = placesRef.current.find((p) => p.id === placeHit.properties.id);
        if (place) setSelection({ type: "place", place });
        return;
      }
      const frontHit = map.queryRenderedFeatures(near(e.point, 5), { layers: FRONT_LAYERS })[0];
      if (frontHit) {
        const front = bufferRef.current?.fronts.find((f) => f.id === frontHit.properties.id);
        if (front) setSelection({ type: "front", front });
        return;
      }
      const countries = map.queryRenderedFeatures(e.point, { layers: COUNTRY_LAYERS });
      const occupied = countries.find((f) => f.properties.control === "occupied");
      const sovereign =
        countries.find((f) => f.properties.control !== "occupied" && f.properties.claim === "de_facto") ||
        countries.find((f) => f.properties.claim !== "de_facto");
      if (!occupied && !sovereign) {
        setSelection(null);
        return;
      }
      // Occupied land opens its de jure owner, noting the occupier (Poland
      // in 1940, occupied by Germany). With no owner recorded underneath,
      // the occupier's card opens instead.
      if (occupied && sovereign && sovereign.properties.entity_id !== occupied.properties.entity_id) {
        openCountry(sovereign.properties.entity_id, {
          occupier: occupied.properties.name,
          occupierId: occupied.properties.entity_id,
          sourceUrl: occupied.properties.source_url,
        });
      } else {
        const hit = (sovereign || occupied).properties;
        openCountry(hit.entity_id, null, hit.approx ? { approximate: true, sourceUrl: hit.source_url } : null);
      }
    });

    // Hover: pointer over clickable markers, outline the polity underneath.
    let hovered = -1;
    map.on("mousemove", (e) => {
      const marker = map.queryRenderedFeatures(near(e.point, 6), { layers: [...UNIT_LAYERS, ...EVENT_LAYERS, "history-place", ...FRONT_LAYERS] }).length > 0;
      const country = marker ? null : map.queryRenderedFeatures(e.point, { layers: COUNTRY_LAYERS })[0];
      map.getCanvas().style.cursor = marker || country ? "pointer" : "";
      const id = country ? country.properties.entity_id : -1;
      if (id !== hovered) {
        hovered = id;
        map.setFilter("country-hover", ["==", ["get", "entity_id"], id]);
      }
    });

    return map;
  };

  // ---- date, window and layer changes ----------------------------------------
  useEffect(() => {
    if (!optsRef.current.playing) dayRef.current = isoToDay(at) + 0.5;
    ensureBuffer(at, windowDays);
    renderData();
  }, [at, windowDays, showForces]);

  // A new day can change who holds a polity's offices; refresh its card.
  useEffect(() => {
    const sel = selectionRef.current;
    if (sel?.type === "country" && !playing) openCountry(sel.entity.id, sel.occupation);
  }, [at]);

  // ---- playback: continuous time, a new date each day -------------------------
  useEffect(() => {
    if (!playing) return undefined;
    let raf = 0;
    let last = performance.now();
    let lastDraw = 0;
    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const next = dayRef.current + dt * optsRef.current.speed;
      if (next > isoToDay(MAX_DATE)) {
        setPlaying(false);
        return;
      }
      dayRef.current = next;
      const iso = dayToIso(next);
      if (iso !== optsRef.current.at) setAt(iso);
      if (now - lastDraw > 33) {
        lastDraw = now;
        renderUnits(next);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  const stepDay = (n) => {
    setPlaying(false);
    setAt(clampIso(dayToIso(isoToDay(at) + n + 0.5)));
  };

  // ---- render ----------------------------------------------------------------
  // Panels use the site's design tokens (app/assets/stylesheets/map.css);
  // the only inline colors left are the data colors of sides and units.
  const cardClass = narrow ? "map_card map_card_sheet" : "map_card";
  const close = (
    <button className="map_card_close" onClick={() => setSelection(null)} aria-label="Close">
      ✕
    </button>
  );
  const info = selection?.type === "event" && eventInfo?.id === selection.event.id ? eventInfo : null;
  const sectionHead = (text) => <div className="map_card_section">{text}</div>;
  const swatch = (color) => <span className="map_swatch" style={{ color }}>■ </span>;

  const countryEvents = () => {
    if (selection?.type !== "country") return [];
    const id = selection.entity.id;
    const [from, to] = neededRange(at, windowDays);
    return (bufferRef.current?.events || []).filter((ev) => {
      const s = isoToDay(ev.start_date);
      const e = ev.end_date ? isoToDay(ev.end_date) : s;
      return (
        !ev.era &&
        s <= to &&
        e >= from &&
        ((ev.entity_ids || []).includes(id) || (ev.locations || []).some((l) => l.entity_id === id))
      );
    });
  };

  // "Era in the United States (highlighted)" or "Era across the whole world".
  const eraRegionText = (era) => {
    const ids = eraRegionIds(era);
    if (!ids.length) return "Era across the whole world";
    const names = new Map((bufferRef.current?.boundaries || []).map((b) => [b.entity_id, b.entity_name]));
    const known = ids.map((id) => names.get(id)).filter(Boolean);
    return known.length ? `Era in ${known.join(", ")} (highlighted)` : "Era";
  };

  const countryEras = () => {
    if (selection?.type !== "country") return [];
    const id = selection.entity.id;
    return eras.filter((era) => eraRegionIds(era).includes(id) || (era.entity_ids || []).includes(id));
  };

  const options = (
    <>
      <select value={speed} onChange={(e) => setSpeed(parseInt(e.target.value, 10))} aria-label="Speed">
        {SPEEDS.map((s) => (
          <option key={s.days} value={s.days}>{s.label}</option>
        ))}
      </select>
      <label className="map_option">
        Events within{" "}
        <select value={windowDays} onChange={(e) => setWindowDays(parseInt(e.target.value, 10))}>
          <option value={30}>1 month</option>
          <option value={90}>3 months</option>
          <option value={365}>1 year</option>
          <option value={1825}>5 years</option>
          <option value={9125}>25 years</option>
        </select>
      </label>
      <label className="map_option">
        <input type="checkbox" checked={showForces} onChange={(e) => setShowForces(e.target.checked)} />
        Armies and fleets
      </label>
    </>
  );

  return (
    <div className="map_widget">
      <div className={narrow ? "map_bar map_bar_narrow" : "map_bar"}>
        {!narrow && <h1 className="map_bar_title">Map</h1>}
        <button className="button" onClick={() => stepDay(-1)} aria-label="Previous day">‹</button>
        <input
          type="date"
          className="map_date"
          value={at}
          min={MIN_DATE}
          max={MAX_DATE}
          onChange={(e) => {
            if (!e.target.value) return;
            setPlaying(false);
            setAt(clampIso(e.target.value));
          }}
        />
        <button className="button" onClick={() => stepDay(1)} aria-label="Next day">›</button>
        <button className="button button_primary map_play" onClick={() => setPlaying((p) => !p)}>
          {playing ? "Pause" : "Play"}
        </button>
        {narrow ? (
          <details className="map_options">
            <summary>Options</summary>
            <div className="map_options_body">{options}</div>
          </details>
        ) : (
          <>
            {options}
            <span className="meta">{formatDate(at)}</span>
          </>
        )}
        {/* On phones the status sits beside "Options" instead of taking a row. */}
        <span className="map_status">
          <span className={loading ? "" : "map_hidden"}>loading…</span>
          <span className={`map_status_error${error ? "" : " map_hidden"}`}>{error || " "}</span>
        </span>
      </div>

      <div className="map_canvas">
        <div ref={mountRef} style={{ width: "100%", height: `max(${narrow ? 360 : 480}px, calc(100dvh - ${mapTop}px))` }} />

        {selection?.type === "event" && (
          <div className={cardClass}>
            <div className="map_card_head">
              <div className="map_card_title">{selection.event.name}</div>
              {close}
            </div>
            <div className="meta">
              {selection.event.start_date}
              {selection.event.end_date && selection.event.end_date !== selection.event.start_date
                ? ` → ${selection.event.end_date}`
                : ""}
            </div>
            {info?.places.length > 0 && <div className="meta">{info.places.join(" · ")}</div>}
            {selection.event.era && <div className="map_card_line">{eraRegionText(selection.event)}</div>}
            {info?.part_of.length > 0 && (
              <div className="meta">
                Part of{" "}
                {info.part_of.slice(0, 2).map((p, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && ", "}
                    <a href={p.path}>{p.name}</a>
                  </React.Fragment>
                ))}
              </div>
            )}
            {info?.description && <div className="map_card_line meta">{info.description}</div>}
            {info?.result && (
              <div className="map_card_line">
                <span className="meta">Result</span> {info.result}
              </div>
            )}
            {info?.sides.length > 0 && (
              <>
                {sectionHead(info.sides.length > 1 ? "Sides" : "Involved")}
                {info.sides.slice(0, 4).map((side, i) => (
                  <div key={i} className="map_card_row">
                    {side.side && <span className="meta">{side.side}: </span>}
                    {side.entities.slice(0, 4).map((en, j) => (
                      <React.Fragment key={en.id}>
                        {j > 0 && ", "}
                        <span className="map_flag">{en.flag_url && <img src={en.flag_url} alt="" loading="lazy" />}</span>
                        <a href="#" onClick={(e) => { e.preventDefault(); openCountry(en.id, null); }}>{en.name}</a>
                      </React.Fragment>
                    ))}
                    {side.entities.length > 4 && <span className="meta"> and {side.entities.length - 4} more</span>}
                  </div>
                ))}
              </>
            )}
            {selection.event.people_count > 0 && <div className="map_card_line meta">{selection.event.people_count} linked {selection.event.people_count === 1 ? "person" : "people"}</div>}
            <div className="map_card_links">
              <a href={selection.event.path}>Open event page →</a>
            </div>
          </div>
        )}

        {selection?.type === "place" && (
          <div className={cardClass}>
            <div className="map_card_head">
              <div className="map_card_title">
                <a href={selection.place.path}>{placeNameOn(selection.place, at)}</a>
              </div>
              {close}
            </div>
            <div className="meta">
              {selection.place.kind}
              {placeNameOn(selection.place, at) !== selection.place.name ? ` · today ${selection.place.name}` : ""}
            </div>
            {placeCapitalOf(selection.place, at).length > 0 && (
              <div className="map_card_line">
                Capital of{" "}
                {placeCapitalOf(selection.place, at)
                  .map((id) => bufferRef.current?.boundaries.find((b) => b.entity_id === id)?.entity_name)
                  .filter(Boolean)
                  .join(", ") || "a polity"}{" "}
                on {formatDate(at)}
              </div>
            )}
            <div className="map_card_links">
              <a href={selection.place.path}>Place page →</a>
            </div>
          </div>
        )}

        {selection?.type === "front" && (
          <div className={cardClass}>
            <div className="map_card_head">
              <div className="map_card_title">{selection.front.name || "Front line"}</div>
              {close}
            </div>
            <div className="meta">
              {selection.front.start_date}
              {selection.front.end_date ? ` → ${selection.front.end_date}` : ""}
            </div>
            {[selection.front.left_entity, selection.front.right_entity].some(Boolean) && (
              <div className="map_card_line">
                {[selection.front.left_entity, selection.front.right_entity].map((side, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && " against "}
                    {swatch(forceColorRef.current.side(i ? selection.front.right_side : selection.front.left_side, side?.id))}
                    {side ? (
                      <a href="#" onClick={(e) => { e.preventDefault(); openCountry(side.id, null); }}>{side.name}</a>
                    ) : (
                      "unknown"
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
            {selection.front.note && <div className="map_card_line meta">{selection.front.note}</div>}
            {selection.front.source_url && (
              <div className="map_card_source">
                Approximate, traced from <a href={selection.front.source_url}>this source</a>
              </div>
            )}
            {selection.front.event && (
              <div className="map_card_links">
                <a href={selection.front.event.path}>{selection.front.event.name} →</a>
              </div>
            )}
          </div>
        )}

        {selection?.type === "unit" && (
          <div className={cardClass}>
            <div className="map_card_head">
              <div className="map_card_title">
                {swatch(selection.color)}
                <a href={selection.unit.path}>{selection.unit.name}</a>
              </div>
              {close}
            </div>
            <div className="meta">
              {[unitKindLabel(selection.unit.kind), selection.unit.entity_name, selection.unit.side].filter(Boolean).join(" · ")}
              {unitStrengthOn(selection.unit, at)}
            </div>
            {selection.unit.parent_name && <div className="meta">Part of {selection.unit.parent_name}</div>}
            {unitPlaceOn(selection.unit, at) && (
              <div className="map_card_line">
                {formatDate(at)}: {unitPlaceOn(selection.unit, at)}
              </div>
            )}
            {selection.unit.description && <div className="map_card_line meta">{selection.unit.description}</div>}
            {unitsWithOn(selection.unit, at).length > 0 && (
              <>
                {sectionHead(`With it (${unitsWithOn(selection.unit, at).length})`)}
                {unitsWithOn(selection.unit, at).map((s, i) => (
                  <div key={i} className="map_card_row">
                    <a href={s.path}>{s.name}</a> <span className="meta">· {unitKindLabel(s.kind)}</span>
                  </div>
                ))}
              </>
            )}
            <div className="map_card_links">
              <a href={selection.unit.path}>Unit page →</a>
              {selection.unit.event_path && <a href={selection.unit.event_path}>{selection.unit.event_name} →</a>}
            </div>
          </div>
        )}

        {selection?.type === "country" && (
          <div className={cardClass}>
            <div className="map_card_head">
              <div className="map_card_title">
                <a href={selection.entity.path}>{selection.entity.name}</a>
              </div>
              {close}
            </div>
            <div className="meta">
              {formatDate(at)}
              {selection.entity.start_date || selection.entity.end_date
                ? ` · ${selection.entity.start_date || "?"} → ${selection.entity.end_date || "present"}`
                : ""}
            </div>
            {selection.entity.capitals?.length > 0 && (
              <div className="map_card_line">
                Capital:{" "}
                {selection.entity.capitals.map((c, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && ", "}
                    <a href={c.path}>{c.name}</a>
                  </React.Fragment>
                ))}
              </div>
            )}
            {selection.occupation && (
              <div className="map_card_line map_occupation">
                Here under military occupation by{" "}
                <a href="#" onClick={(e) => { e.preventDefault(); openCountry(selection.occupation.occupierId, null); }}>
                  {selection.occupation.occupier}
                </a>
                .
                {selection.occupation.sourceUrl && (
                  <>
                    {" "}
                    <a href={selection.occupation.sourceUrl}>Source of this area</a>.
                  </>
                )}
              </div>
            )}
            {selection.border?.approximate && (
              <div className="map_card_source">
                Approximate border
                {selection.border.sourceUrl ? (
                  <>
                    , traced from <a href={selection.border.sourceUrl}>this source</a>.
                  </>
                ) : (
                  "."
                )}
              </div>
            )}
            {selection.entity.description && <div className="map_card_line meta">{selection.entity.description}</div>}
            {sectionHead("Leaders")}
            {selection.rulers.length === 0 && <div className="meta">No office holders recorded for this date.</div>}
            {selection.rulers.slice(0, 5).map((r, i) => (
              <div key={i} className="map_ruler">
                {i === 0 && r.portrait_url && (
                  <img
                    src={r.portrait_url}
                    alt={r.person_name}
                    onError={(e) => {
                      e.currentTarget.hidden = true;
                    }}
                  />
                )}
                <div>
                  <a href={r.person_path}>{r.person_name}</a>
                  <div className="meta">{r.office}</div>
                  {i === 0 && (
                    <div className="meta">
                      {r.start_date || "?"} → {r.end_date || "present"}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {countryEras().length > 0 && (
              <>
                {sectionHead("Eras")}
                {countryEras().map((era) => (
                  <div key={era.id} className="map_card_row">
                    <a href={era.path}>{era.name}</a>
                    <span className="meta"> · {eraYears(era)}</span>
                  </div>
                ))}
              </>
            )}
            {sectionHead(`Events in this period (${countryEvents().length})`)}
            {countryEvents().length === 0 && <div className="meta">No recorded events in the selected window.</div>}
            {countryEvents()
              .slice(0, 10)
              .map((ev) => (
                <div key={ev.id} className="map_card_row">
                  <a href={ev.path}>{ev.name}</a>
                  <span className="meta"> · {ev.start_date}</span>
                </div>
              ))}
          </div>
        )}

        {eras.length > 0 && !(narrow && selection) && (
          <div className="map_eras">
            <button className="map_eras_head" onClick={() => setErasOpen((o) => !o)} aria-expanded={erasOpen}>
              Eras ({eras.length}) {erasOpen ? "▾" : "▸"}
            </button>
            {erasOpen &&
              eras.slice(0, ERAS_LISTED).map((era) => (
                <button
                  key={era.id}
                  className={`map_era${selection?.type === "event" && selection.event.id === era.id ? " map_era_selected" : ""}`}
                  onClick={() => setSelection({ type: "event", event: era })}
                >
                  <span>{era.name}</span>
                  <span className="meta">{eraYears(era)}{eraRegionIds(era).length ? "" : " · world"}</span>
                </button>
              ))}
          </div>
        )}
      </div>

      <p className="map_hint meta">
        {COARSE
          ? "Drag to pan, pinch to zoom. Tap a country for its leaders and events, a marker for its event, or"
          : "Drag to pan, scroll or pinch to zoom. Click a country for its leaders and events, a marker for its event, or"}{" "}
        an army (box) or fleet (ship) for its details. Eras (long periods such as the Cold War) are listed in the corner for the countries in view. Hatched areas are under military occupation; a double line is a front, in each side's color. Borders cover
        tracked entities only.
      </p>
    </div>
  );
}
